import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, ShieldCheck, Search, Filter, User as UserIcon, Building2, Undo2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PAGE_SIZE = 100;

const ACTION_LABELS = {
  'asset.create': { label: 'Activo creado', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'asset.update': { label: 'Activo editado', tone: 'bg-blue-50 text-blue-700 border-blue-200' },
  'asset.trash': { label: 'Activo enviado a papelera', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  'asset.delete': { label: 'Activo eliminado', tone: 'bg-red-50 text-red-700 border-red-200' },
  'asset.bulk_estado': { label: 'Cambio de estado masivo', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  'asset.bulk_restore': { label: 'Restauración masiva', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'asset.refresh_valor_base': { label: 'Valor base refrescado', tone: 'bg-slate-50 text-slate-700 border-slate-200' },
  'assets.bulk_recalc_scores': { label: 'Recalcular scores (lote)', tone: 'bg-slate-50 text-slate-700 border-slate-200' },
  'import.excel': { label: 'Importación Excel', tone: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  'catastro.consult': { label: 'Consulta Catastro', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  'catastro.enrich_selected': { label: 'SOLICITAR DATOS A CATASTRO', tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  'price_m2.update': { label: 'Precio €/m² editado', tone: 'bg-purple-50 text-purple-700 border-purple-200' },
  'price_m2.paste_idealista': { label: 'Precios Idealista pegados', tone: 'bg-purple-50 text-purple-700 border-purple-200' },
  'papelera.empty': { label: 'Papelera vaciada', tone: 'bg-red-50 text-red-700 border-red-200' },
  'audit.undo': { label: 'Cambio deshecho', tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'asset.review_flag': { label: 'Revisión del activo', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'medium' }); }
  catch { return iso; }
};

const MetaPretty = ({ meta }) => {
  if (!meta || Object.keys(meta).length === 0) return <span className="text-slate-400 italic">—</span>;
  // Render changes nicely if present
  const entries = [];
  if (meta.changes && typeof meta.changes === 'object') {
    for (const [k, v] of Object.entries(meta.changes)) {
      if (v && typeof v === 'object' && 'from' in v && 'to' in v) {
        entries.push(
          <div key={k} className="text-xs">
            <span className="font-medium text-slate-700">{k}</span>:{' '}
            <span className="line-through text-slate-400">{String(v.from ?? '—')}</span>{' → '}
            <span className="text-emerald-700 font-medium">{String(v.to ?? '—')}</span>
          </div>
        );
      }
    }
  }
  const otherKeys = Object.keys(meta).filter(k => k !== 'changes');
  for (const k of otherKeys) {
    const v = meta[k];
    if (v === null || v === undefined || v === '') continue;
    const show = (typeof v === 'object') ? JSON.stringify(v) : String(v);
    entries.push(<div key={k} className="text-xs text-slate-600"><span className="font-medium">{k}</span>: {show}</div>);
  }
  return <div className="space-y-0.5">{entries}</div>;
};

const AuditoriaPage = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [undoingId, setUndoingId] = useState(null);

  const handleUndo = async (event) => {
    if (!event?.is_undoable) return;
    const lbl = (ACTION_LABELS[event.action] || { label: event.action }).label;
    if (!window.confirm(`¿Deshacer "${lbl}" del ${new Date(event.created_at).toLocaleString('es-ES')}?\n\nSe revertirán los cambios y quedará registrado en el log.`)) return;
    setUndoingId(event.id);
    try {
      const r = await axios.post(`${API}/audit-logs/${event.id}/undo`, {}, { withCredentials: true });
      toast.success(r.data?.summary || 'Cambio deshecho correctamente');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo deshacer');
    } finally { setUndoingId(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', String(page * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));
      if (q) params.append('q', q);
      if (actionFilter) params.append('action', actionFilter);
      const r = await axios.get(`${API}/audit-logs?${params.toString()}`, { withCredentials: true });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Solo admin puede ver auditoría');
      else toast.error('Error al cargar auditoría');
    } finally { setLoading(false); }
  }, [page, q, actionFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="auditoria-page">
      <div>
        <h1 className="heading-1 flex items-center gap-3"><ShieldCheck className="w-7 h-7 text-blue-600" /> Auditoría</h1>
        <p className="body-text mt-1">Registro completo de acciones del sistema. Retención indefinida.</p>
      </div>

      <div className="card-base p-4 flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={q} onChange={e => { setQ(e.target.value); setPage(0); }} placeholder="Buscar por usuario, acción o entidad..." className="pl-10 input-base" data-testid="audit-search" />
        </div>
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <select value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }} className="pl-9 input-base" data-testid="audit-action-filter">
            <option value="">Todas las acciones</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
      </div>

      {loading && <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-blue-600 animate-spin" /></div>}

      {!loading && items.length === 0 && (
        <div className="card-base p-12 text-center">
          <ShieldCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="heading-3 mb-2">Sin registros</h3>
          <p className="body-text">No hay eventos de auditoría para los filtros aplicados.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: '900px', borderCollapse: 'collapse', width: '100%' }} data-testid="audit-table">
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                <th className="table-header" style={{ width: '160px' }}>Fecha / Hora</th>
                <th className="table-header" style={{ width: '170px' }}>Acción</th>
                <th className="table-header" style={{ width: '140px' }}>Usuario</th>
                <th className="table-header" style={{ width: '100px' }}>Activo</th>
                <th className="table-header">Detalles</th>
                <th className="table-header" style={{ width: '110px' }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {items.map(row => {
                const action = ACTION_LABELS[row.action] || { label: row.action, tone: 'bg-slate-50 text-slate-700 border-slate-200' };
                return (
                  <tr key={row.id} className="table-row align-top" data-testid={`audit-row-${row.id}`}>
                    <td className="table-cell text-xs text-slate-500" style={{ whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
                    <td className="table-cell">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${action.tone}`}>
                        {action.label}
                      </span>
                      {row.undone_at && (
                        <span className="block text-[10px] text-emerald-600 mt-1">↩ Deshecho {fmtDate(row.undone_at)}{row.undone_by_name ? ` por ${row.undone_by_name}` : ''}</span>
                      )}
                    </td>
                    <td className="table-cell text-xs">
                      <div className="flex items-center gap-1.5 text-slate-700">
                        <UserIcon className="w-3 h-3 text-slate-400" />
                        {row.user_name || 'sistema'}
                      </div>
                      {row.user_email && <p className="text-[10px] text-slate-400">{row.user_email}</p>}
                    </td>
                    <td className="table-cell text-xs">
                      {row.asset_id ? (
                        <Link to={`/activos/${row.asset_id}`} className="text-blue-600 hover:underline inline-flex items-center gap-1" title={row.asset_id}>
                          <Building2 className="w-3 h-3" /> abrir
                        </Link>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="table-cell"><MetaPretty meta={row.meta} /></td>
                    <td className="table-cell text-xs">
                      {row.is_undoable ? (
                        <button
                          type="button"
                          onClick={() => handleUndo(row)}
                          disabled={undoingId === row.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50 text-xs font-medium"
                          title="Revertir este cambio"
                          data-testid={`audit-undo-${row.id}`}
                        >
                          {undoingId === row.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />}
                          Deshacer
                        </button>
                      ) : (
                        <span className="text-slate-300 text-[11px]" title={row.undone_at ? 'Ya deshecho' : 'No reversible'}>
                          {row.undone_at ? <RotateCcw className="w-3 h-3 inline mr-1" /> : null}{row.undone_at ? 'Deshecho' : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > PAGE_SIZE && (
        <div className="flex justify-end items-center gap-2 text-sm text-slate-600">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="btn-secondary text-sm disabled:opacity-40" data-testid="audit-prev">Anterior</button>
          <span>Pagina {page + 1} de {totalPages} ({total} eventos)</span>
          <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page + 1 >= totalPages} className="btn-secondary text-sm disabled:opacity-40" data-testid="audit-next">Siguiente</button>
        </div>
      )}
    </div>
  );
};

export default AuditoriaPage;
